import i18n from 'i18next';
import jsCookie from 'js-cookie';
import { Middleware } from 'redux';
import LikeService from 'services/like.service';
import { USER_SET } from 'store/action-types/user.action-types';
import { likeToggled, likeToggledLocally } from 'store/actions/like-service.actions';
import { openLoginModal, setLoginModalStoreData } from 'store/actions/login.actions';
import { setShopFollowState } from 'store/actions/shop-follow.actions';
import { GTM, ShopFollowAnalytics } from 'utils/analytics';
import { notification } from 'utils/snackbar';

export const likeServiceMiddleware: Middleware = ({ getState, dispatch }) => {
  const REQUEST_TIMEOUT = 500;

  const timeout = () => new Promise((resolve) => setTimeout(resolve, REQUEST_TIMEOUT));

  const handleLikeData = async (
    likeData: {
      id: number;
      liked: boolean;
      searchGuid?: string;
      recommendationId?: string;
      recommendationEngine?: string;
      analytics?: ShopFollowAnalytics;
    },
    shouldBeDelayed: boolean,
    type = LikeService.PRODUCT_TYPE,
  ) => {
    if (!likeData?.id) {
      return;
    }

    // provide fast visual response for user
    dispatch(likeToggledLocally(likeData));

    if (shouldBeDelayed) {
      await timeout();
    }

    const likeService = new LikeService();
    const response = await likeService.changeLikeStatus(likeData, type);

    if (!response) {
      console.error('Failed to toggle like status');
      // toggle local state back to keep consistency with the real data
      dispatch(
        likeToggledLocally({
          ...likeData,
          liked: !likeData.liked,
        }),
      );

      return;
    }

    if (type === LikeService.USER_TYPE) {
      setShopFollowState(likeData.id, likeData.liked);

      if (likeData.analytics) {
        GTM.shopFollowToggled(likeData.analytics, likeData.liked, true);
      }
    }

    if (response?.showLikeNotification) {
      notification.showSuccessNotification(i18n.t('pages.products.like-notification'));
    }
  };

  return (next) => async (action) => {
    next(action);

    switch (action.type) {
      case USER_SET: {
        // the case when user liked some product before login
        // and this action is saved and applied after successful auth
        const likeService = new LikeService();
        const likeData = typeof window !== 'undefined' ? await likeService.getLocalStorageLike() : null;

        // if there is no token at this moment then probably race condition took a place for the cookie
        // the authorized request should be delayed.
        const shouldBeDelayed = !jsCookie.get('token');

        handleLikeData(likeData, shouldBeDelayed, likeData?.type);

        break;
      }
      case likeToggled.type: {
        const likedData: { id: number; liked: boolean; searchGuid?: string; recommendationId?: string; recommendationEngine?: string } =
          action.payload;

        const { userData } = getState();
        const { user } = userData;

        const isUserLoggedIn = !!user?.isLoggedIn;

        if (!isUserLoggedIn) {
          setLoginModalStoreData(likedData);
          openLoginModal(true);

          break;
        }

        handleLikeData(likedData, false);

        break;
      }
    }
  };
};
